import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import NotificationSnackbar from "./NotificationBar";
const INITIAL_FORM = {
  id: "",
  name: "",
  tin: "",
  ifmisCode: "",
  countryId: "",
  currencyId: "",
};
function Addnewcostcenter({ open, onClose, onSubmit }) {
  const theme = useTheme();
  const [form, setForm] = useState(INITIAL_FORM);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const response = await axios.get("/api/support/countries");
        setCountries(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching countries:", err);
        setSnackbarMessage("Failed to load countries.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingCountries(false);
      }
    };
    const fetchCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await axios.get("/api/support/currencies");
        setCurrencies(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching currencies:", err);
        setSnackbarMessage("Failed to load currencies.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCountries();
    fetchCurrencies();
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryChange = (event, selectedCountry) => {
    setForm((prev) => ({
      ...prev,
      countryId: selectedCountry ? selectedCountry.id : "",
    }));
  };
  const handleCurrencyChange = (event, selectedCurrency) => {
    setForm((prev) => ({
      ...prev,
      currencyId: selectedCurrency ? selectedCurrency.id : "",
    }));
  };
  const selectedCountry =
    countries.find(
      (country) => String(country.id) === String(form.countryId),
    ) || null;

  const selectedCurrency =
    currencies.find(
      (currency) => String(currency.id) === String(form.currencyId),
    ) || null;
  const handleSubmit = async () => {
    if (
      !form.id ||
      !form.name ||
      !form.tin ||
      !form.ifmisCode ||
      !form.countryId ||
      !form.currencyId
    ) {
      setSnackbarMessage("Please fill in all required fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      setSaving(true);
      const costcenterData = {
        id: form.id,
        name: form.name,
        tin: form.tin,
        ifmisCode: form.ifmisCode,
        countryId: form.countryId,
        currencyId: form.currencyId,
      };
      const response = await axios.post(
        "/api/support/costcenter",
        costcenterData,
      );
      setSnackbarMessage("Costcenter created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        if (onSubmit) {
          onSubmit(response.data);
        }
        setForm(INITIAL_FORM);
        onClose();
      }, 1000);
    } catch (err) {
      console.error("Error creating costcenter:", err);
      const message =
        err.response?.data?.message || "Failed to create costcenter.";
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
        },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          py: 1.5,
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
        }}
      >
        Add New Cost Center
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={saving}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: theme.palette.primary.contrastText,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Grid container spacing={1.5}>
          {/* ID */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="ID"
              name="id"
              value={form.id}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>

          {/* COST CENTER NAME */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Cost Center Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>

          {/* TIN */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="TIN Number"
              name="tin"
              value={form.tin}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>

          {/* IFMIS CODE */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="IFMIS Code"
              name="ifmisCode"
              value={form.ifmisCode}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>

          {/* COUNTRY */}
          <Grid item xs={12}>
            <Autocomplete
              options={countries}
              value={selectedCountry}
              onChange={handleCountryChange}
              loading={loadingCountries}
              disabled={saving}
              getOptionLabel={(option) => option.Country || ""}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              noOptionsText="No countries found"
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Country"
                  placeholder="Search country..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCountries && (
                          <CircularProgress color="inherit" size={18} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 250,
                },
              }}
            />
          </Grid>

          {/* CURRENCY */}
          <Grid item xs={12}>
            <Autocomplete
              options={currencies}
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              loading={loadingCurrencies}
              disabled={saving}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              noOptionsText="No currencies found"
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label="Currency"
                  placeholder="Search currency..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCurrencies && (
                          <CircularProgress color="inherit" size={18} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 250,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={
            saving ||
            !form.id ||
            !form.name ||
            !form.tin ||
            !form.ifmisCode ||
            !form.countryId ||
            !form.currencyId
          }
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>

      {/* SNACKBAR NOTIFICATION COMPONENT */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default Addnewcostcenter;
